import React, {Component} from 'react';
import {observable, action, computed} from 'mobx';
import {inject, observer} from 'mobx-react';
import {toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment';
import {
  Row, Col, Card, CardBody, FormInput, FormCheckbox,
  Button, Modal, ModalHeader, ModalBody
} from 'shards-react';
import FeatherIcon from 'feather-icons-react';
import {Link} from 'react-router-dom';

import PageTitle from '../../../components/common/PageTitle';
import ModalLoading from '../../../components/common/ModalLoading';
import './style.scss';

@inject(({teamUser}) => ({isLogin: teamUser.isLogin, me: teamUser.me}))
@inject(({product}) => ({getProductList: product.getProductList}))
@inject(({review}) => ({
  getReviewsForCopy: review.getReviewsForCopy,
  getCopiedSourceMap: review.getCopiedSourceMap,
  runReviewCopy: review.runReviewCopy
}))
@observer
class ReviewCopy extends Component {

  // --- basic ---
  @observable isLogin = false;
  @observable teamUser = {};
  @observable store = {};
  @observable isLoading = false;

  // --- 유의사항 동의 ---
  @observable showWarning = true;
  @observable warningAgreed = false;
  WARNING_STORAGE_KEY = 'reviewCopy_warningAgreed';

  // --- step state ---
  @observable activeStep = 1;

  // --- product selection ---
  @observable sourceProduct = null;       // {product_id, platformProductId, productName}
  @observable targetProducts = [];        // array of {product_id, platformProductId, productName}

  @observable productSearch = {
    side: null,              // 'source' | 'target' (열린 모달의 사이드)
    isOpen: false,
    keyword: '',
    isSearching: false,
    results: [],
    page: 1,
    totalPages: 1
  };

  // --- review selection ---
  @observable reviewList = {docs: [], totalDocs: 0, page: 1, totalPages: 1};
  @observable reviewParams = {page: 1, limit: 20};
  @observable reviewFilters = {keyword: '', ratings: []};      // ratings: [1..5]
  @observable selectedReviewIds = [];
  @observable copiedSourceMap = {};   // {sourceReview_id: [targetPlatformProductId, ...]}

  // --- options ---
  @observable options = {
    keepWriter: true,
    keepWriterAt: true,
    includeImages: true
  };

  // --- progress ---
  @observable progress = {
    isOpen: false,
    isRunning: false,
    isCancelRequested: false,
    jobId: null,
    chunkSize: 50,
    chunkIndex: 0,
    chunkTotal: 0,
    totalCount: 0,
    successCount: 0,
    failCount: 0,
    skipCount: 0,
    log: []     // {time, msg}
  };

  componentDidMount() {
    this.init();
    this.checkWarningAgreement();
  }

  @action init = async () => {
    if (this.props.isLogin && this.props.me) {
      this.isLogin = true;
      this.teamUser = this.props.me;
      this.store = this.props.me.store || {};
    }
  };

  // ============== 유의사항 동의 ==============

  @action checkWarningAgreement = () => {
    try {
      const agreed = sessionStorage.getItem(this.WARNING_STORAGE_KEY);
      if (agreed === 'true') {
        this.showWarning = false;
        this.warningAgreed = true;
      }
    } catch (e) {
      console.warn('sessionStorage 사용 불가, 메모리 모드로 동작');
    }
  };

  @action toggleWarningAgree = () => {
    this.warningAgreed = !this.warningAgreed;
  };

  @action dismissWarning = () => {
    if (!this.warningAgreed) return;
    this.showWarning = false;
    try {
      sessionStorage.setItem(this.WARNING_STORAGE_KEY, 'true');
    } catch (e) {
      console.warn('sessionStorage 저장 실패:', e);
    }
  };

  // ============== 상품 검색/선택 ==============

  @action openProductSearch = (side) => {
    this.productSearch = {side, isOpen: true, keyword: '', isSearching: false, results: [], page: 1, totalPages: 1};
    this.searchProducts();
  };

  @action closeProductSearch = () => {
    this.productSearch.isOpen = false;
  };

  @action onChangeProductKeyword = (e) => {
    this.productSearch.keyword = e.target.value;
  };

  @action searchProducts = async () => {
    if (!this.teamUser.selStore_id) return;
    this.productSearch.isSearching = true;
    try {
      const res = await this.props.getProductList({
        store_id: this.teamUser.selStore_id,
        shopNo: this.teamUser.selShopNo || 1,
        page: this.productSearch.page,
        limit: 30,
        searchParams: this.productSearch.keyword ? {keyword: this.productSearch.keyword} : {}
      });
      this.productSearch.results = (res && res.docs) ? res.docs : [];
      this.productSearch.totalPages = (res && res.totalPages) || 1;
    } catch (e) {
      toast.error('상품 검색 실패');
    } finally {
      this.productSearch.isSearching = false;
    }
  };

  @action goProductPage = (page) => {
    if (page < 1 || page > this.productSearch.totalPages) return;
    this.productSearch.page = page;
    this.searchProducts();
  };

  @action pickProduct = (p) => {
    const pInfo = {
      product_id: p.product_id || p._id || String(p.platformProductId),
      platformProductId: String(p.platformProductId),
      productName: p.productName || '',
      image: p.image || (p.images && (p.images.small || p.images.thumbnail || p.images.tiny || p.images.detail)) || ''
    };
    if (this.productSearch.side === 'source') {
      // 원본은 단일 — 변경 시 리뷰 선택 초기화
      if (this.sourceProduct && this.sourceProduct.platformProductId !== pInfo.platformProductId) {
        this.selectedReviewIds = [];
        this.reviewList = {docs: [], totalDocs: 0, page: 1, totalPages: 1};
      }
      this.sourceProduct = pInfo;
    } else {
      // 대상은 다중 — 토글
      const idx = this.targetProducts.findIndex(t => t.platformProductId === pInfo.platformProductId);
      if (idx >= 0) this.targetProducts.splice(idx, 1);
      else this.targetProducts.push(pInfo);
    }
  };

  @action removeTargetProduct = (pid) => {
    this.targetProducts = this.targetProducts.filter(t => t.platformProductId !== pid);
  };

  isTargetPicked = (pid) => {
    return !!this.targetProducts.find(t => t.platformProductId === pid);
  };

  // ============== 리뷰 조회 ==============

  @action loadReviews = async () => {
    if (!this.sourceProduct) return;
    this.isLoading = true;
    try {
      const ratings = this.reviewFilters.ratings.length ? this.reviewFilters.ratings : null;
      const res = await this.props.getReviewsForCopy({
        store_id: this.teamUser.selStore_id,
        shopNo: this.teamUser.selShopNo || 1,
        sourcePlatformProductId: this.sourceProduct.platformProductId,
        page: this.reviewParams.page,
        limit: this.reviewParams.limit,
        ratings: ratings,
        keyword: this.reviewFilters.keyword || ''
      });
      const data = (res && res.data) ? res.data : {docs: [], totalDocs: 0, totalPages: 1, page: 1};
      this.reviewList = {
        docs: data.docs || [],
        totalDocs: data.totalDocs || 0,
        page: data.page || 1,
        totalPages: data.totalPages || 1
      };
      // 복사 맵 갱신 (대상 상품이 1개 이상일 때만)
      await this.refreshCopiedMap();
    } catch (e) {
      toast.error('리뷰 로드 실패');
    } finally {
      this.isLoading = false;
    }
  };

  @action refreshCopiedMap = async () => {
    if (!this.sourceProduct || !this.targetProducts.length) {
      this.copiedSourceMap = {};
      return;
    }
    try {
      const res = await this.props.getCopiedSourceMap({
        store_id: this.teamUser.selStore_id,
        sourcePlatformProductId: this.sourceProduct.platformProductId,
        targetPlatformProductIds: this.targetProducts.map(t => t.platformProductId)
      });
      this.copiedSourceMap = (res && res.data && res.data.sourceMap) || {};
    } catch (e) {
      this.copiedSourceMap = {};
    }
  };

  isCopiedToAllTargets = (reviewId) => {
    const arr = this.copiedSourceMap[reviewId];
    if (!arr || !arr.length) return false;
    return this.targetProducts.every(t => arr.includes(t.platformProductId));
  };

  isCopiedToAny = (reviewId) => {
    const arr = this.copiedSourceMap[reviewId];
    return !!(arr && arr.length);
  };

  @action toggleSelectReview = (reviewId) => {
    if (this.isCopiedToAllTargets(reviewId)) return;
    const idx = this.selectedReviewIds.indexOf(reviewId);
    if (idx >= 0) this.selectedReviewIds.splice(idx, 1);
    else this.selectedReviewIds.push(reviewId);
  };

  @action toggleSelectAllOnPage = () => {
    const eligible = this.reviewList.docs
      .filter(r => !this.isCopiedToAllTargets(String(r._id)))
      .map(r => String(r._id));
    const allSelected = eligible.every(id => this.selectedReviewIds.includes(id));
    if (allSelected) {
      this.selectedReviewIds = this.selectedReviewIds.filter(id => !eligible.includes(id));
    } else {
      const next = new Set([...this.selectedReviewIds, ...eligible]);
      this.selectedReviewIds = Array.from(next);
    }
  };

  @action selectByRating = (rating) => {
    // 현재 페이지 기준 별점 N 만 선택에 추가
    const eligible = this.reviewList.docs
      .filter(r => Math.round((r.goodsPt || 0) / 20) === rating && !this.isCopiedToAllTargets(String(r._id)))
      .map(r => String(r._id));
    const next = new Set([...this.selectedReviewIds, ...eligible]);
    this.selectedReviewIds = Array.from(next);
  };

  @action clearSelectedReviews = () => {
    this.selectedReviewIds = [];
  };

  @action onChangeReviewFilter = (e) => {
    this.reviewFilters.keyword = e.target.value;
  };

  @action toggleRatingFilter = (rating) => {
    const idx = this.reviewFilters.ratings.indexOf(rating);
    if (idx >= 0) this.reviewFilters.ratings.splice(idx, 1);
    else this.reviewFilters.ratings.push(rating);
  };

  @action goReviewPage = (page) => {
    if (page < 1 || page > this.reviewList.totalPages) return;
    this.reviewParams.page = page;
    this.loadReviews();
  };

  // ============== Step navigation ==============

  @action goStep = (step) => {
    if (step === 2) {
      if (!this.sourceProduct) { toast.warn('원본 상품을 선택해 주세요.'); return; }
      if (!this.targetProducts.length) { toast.warn('대상 상품을 1개 이상 선택해 주세요.'); return; }
      // 동일 상품 복사 차단
      if (this.targetProducts.find(t => t.platformProductId === this.sourceProduct.platformProductId)) {
        toast.warn('원본과 대상 상품이 동일할 수 없습니다.');
        return;
      }
      this.activeStep = 2;
      this.reviewParams.page = 1;
      this.loadReviews();
      return;
    }
    if (step === 3) {
      if (!this.selectedReviewIds.length) { toast.warn('복사할 리뷰를 1건 이상 선택해 주세요.'); return; }
      this.activeStep = 3;
      return;
    }
    this.activeStep = step;
  };

  // ============== 복사 실행 (50건 청크 반복) ==============

  @computed get totalToCopy() {
    return this.selectedReviewIds.length * this.targetProducts.length;
  }

  // 청크당 카페24 호출 수(source × target)를 제한 — 서버측 레이트리밋 스로틀 도입으로
  // 청크가 크면 처리 시간이 길어져 타임아웃 위험. 청크당 최대 ~25건으로 동적 조정
  @computed get effectiveChunkSize() {
    const targetCount = Math.max(1, this.targetProducts.length);
    return Math.max(1, Math.min(this.progress.chunkSize, Math.floor(25 / targetCount) || 1));
  }

  @action openProgressAndRun = async () => {
    const total = this.totalToCopy;
    const chunkSize = this.effectiveChunkSize;
    // 청크 = sourceReview 단위로 끊음 (target은 내부 반복)
    const sourceChunks = [];
    for (let i = 0; i < this.selectedReviewIds.length; i += chunkSize) {
      sourceChunks.push(this.selectedReviewIds.slice(i, i + chunkSize));
    }

    this.progress = {
      ...this.progress,
      isOpen: true,
      isRunning: true,
      isCancelRequested: false,
      jobId: null,
      chunkIndex: 0,
      chunkTotal: sourceChunks.length,
      totalCount: total,
      successCount: 0,
      failCount: 0,
      skipCount: 0,
      log: [{time: moment().format('HH:mm:ss'), msg: `복사 시작 — 총 ${total}건 (${sourceChunks.length}청크)`}]
    };

    const actorName = (this.teamUser && (this.teamUser.name || this.teamUser.userName)) || '';
    const actorMemberId = (this.teamUser && (this.teamUser.platformUserId || this.teamUser.emailId)) || '';

    // 리뷰 부스터 유료 애드온 권한 체크
    if (!this.teamUser.hasReviewWriteAddon) {
      toast.error('리뷰 부스터 추가 옵션 결제가 필요합니다. 결제 페이지에서 옵션을 활성화해주세요.');
      return;
    }

    let jobId = null;
    for (let i = 0; i < sourceChunks.length; i++) {
      if (this.progress.isCancelRequested) {
        this.appendLog('사용자 중단 요청 — 종료');
        break;
      }
      this.progress.chunkIndex = i + 1;
      const chunk = sourceChunks[i];
      const isLast = i === sourceChunks.length - 1;
      try {
        const res = await this.props.runReviewCopy({
          jobId: jobId,
          store_id: this.teamUser.selStore_id,
          shopNo: this.teamUser.selShopNo || 1,
          sourceProduct_id: this.sourceProduct.product_id,
          sourcePlatformProductId: this.sourceProduct.platformProductId,
          sourceProductName: this.sourceProduct.productName,
          sourceReview_ids: chunk,
          targetProducts: this.targetProducts.map(t => ({
            product_id: t.product_id,
            platformProductId: t.platformProductId,
            productName: t.productName
          })),
          options: {
            keepWriter: this.options.keepWriter,
            keepWriterAt: this.options.keepWriterAt,
            includeImages: this.options.includeImages,
            actorName: actorName,
            actorMemberId: actorMemberId
          },
          totalCount: total,
          isLast: isLast && !this.progress.isCancelRequested
        });
        const data = (res && res.data) ? res.data : null;
        if (!data || data.error) {
          this.progress.failCount += chunk.length * this.targetProducts.length;
          this.appendLog(`청크 ${i + 1} 실패: ${(data && data.error) || 'unknown'}`);
        } else {
          jobId = data.jobId;
          this.progress.jobId = jobId;
          this.progress.successCount += data.successCount || 0;
          this.progress.failCount += data.failCount || 0;
          const skipped = (data.processed || []).filter(p => p.status === 'skip').length;
          this.progress.skipCount += skipped;
          this.appendLog(`청크 ${i + 1}/${sourceChunks.length} 완료 — 성공 ${data.successCount || 0}, 실패 ${data.failCount || 0}, 스킵 ${skipped}`);
        }
      } catch (e) {
        this.progress.failCount += chunk.length * this.targetProducts.length;
        this.appendLog(`청크 ${i + 1} 예외: ${e.message || e}`);
      }
    }

    this.progress.isRunning = false;
    this.appendLog('완료');
    // 복사 맵 새로고침
    await this.refreshCopiedMap();
    // 선택 해제 (이미 복사된 상태)
    this.selectedReviewIds = [];
  };

  @action appendLog = (msg) => {
    this.progress.log.push({time: moment().format('HH:mm:ss'), msg});
  };

  @action requestCancel = () => {
    this.progress.isCancelRequested = true;
    this.appendLog('중단 요청됨 — 진행 중인 청크 완료 후 종료됩니다.');
  };

  @action closeProgress = () => {
    if (this.progress.isRunning) return;
    this.progress.isOpen = false;
  };

  // ============== 렌더 ==============

  renderThumb(url) {
    if (url) {
      return <img className="copy-product-thumb" src={url} alt="" onError={e => { e.target.style.visibility = 'hidden'; }}/>;
    }
    return <span className="copy-product-thumb copy-product-thumb--empty"><FeatherIcon icon="image" size="14"/></span>;
  }

  // 리뷰의 이미지 URL 추출 — platformImages > images > platformData.attached_file_detail
  extractReviewImages(review) {
    const collect = [];
    const push = (raw) => {
      if (!raw) return;
      if (typeof raw === 'string') collect.push(raw);
      else if (typeof raw === 'object') {
        const u = raw.url || raw.path || raw.src || raw.attach_file_url;
        if (typeof u === 'string') collect.push(u);
      }
    };
    const sources = [review.platformImages, review.images];
    for (const s of sources) {
      if (!s) continue;
      if (Array.isArray(s)) s.forEach(push);
      else if (typeof s === 'object') Object.values(s).forEach(push);
      if (collect.length) break;
    }
    if (!collect.length && review.platformData && review.platformData.attached_file_detail) {
      const afd = review.platformData.attached_file_detail;
      if (Array.isArray(afd)) afd.forEach(push);
      else if (typeof afd === 'object') Object.values(afd).forEach(push);
    }
    return collect.filter(u => /^https?:\/\//i.test(u));
  }

  renderRatingStars(goodsPt) {
    const star = Math.round((goodsPt || 0) / 20);
    return (
      <span className="copy-rating">
        {[1, 2, 3, 4, 5].map(n => (
          <span key={n} className={n <= star ? 'star-on' : 'star-off'}>★</span>
        ))}
      </span>
    );
  }

  renderStep1 = () => (
    <div className="review-copy-step">
      <h4 className="copy-step-title">STEP 1. 원본 / 대상 상품 선택</h4>
      <Row>
        <Col md="6">
          <Card className="copy-product-card">
            <CardBody>
              <div className="copy-product-card__head">
                <span className="copy-product-card__label">원본 상품 (A)</span>
                <Button size="sm" theme="light" onClick={() => this.openProductSearch('source')}>
                  <FeatherIcon icon="search" size="14"/> 상품 선택
                </Button>
              </div>
              {this.sourceProduct ? (
                <div className="copy-picked-list">
                  <div className="copy-picked-item">
                    {this.renderThumb(this.sourceProduct.image)}
                    <span className="copy-picked-item__name">{this.sourceProduct.productName}</span>
                    <span className="copy-picked-item__pid">{this.sourceProduct.platformProductId}</span>
                    <button className="copy-picked-item__del" onClick={() => { this.sourceProduct = null; this.selectedReviewIds = []; this.reviewList = {docs: [], totalDocs: 0, page: 1, totalPages: 1}; }}>
                      <FeatherIcon icon="x" size="14"/>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="copy-product-empty">단일 상품을 선택하세요.</div>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card className="copy-product-card">
            <CardBody>
              <div className="copy-product-card__head">
                <span className="copy-product-card__label">대상 상품 (B) — 다중 선택</span>
                <Button size="sm" theme="light" onClick={() => this.openProductSearch('target')}>
                  <FeatherIcon icon="search" size="14"/> 상품 선택
                </Button>
              </div>
              {this.targetProducts.length ? (
                <div className="copy-picked-list">
                  {this.targetProducts.map(t => (
                    <div key={t.platformProductId} className="copy-picked-item">
                      {this.renderThumb(t.image)}
                      <span className="copy-picked-item__name">{t.productName}</span>
                      <span className="copy-picked-item__pid">{t.platformProductId}</span>
                      <button className="copy-picked-item__del" onClick={() => this.removeTargetProduct(t.platformProductId)}>
                        <FeatherIcon icon="x" size="14"/>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="copy-product-empty">하나 이상의 상품을 선택하세요.</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <div className="copy-step-actions">
        <Button onClick={() => this.goStep(2)}>
          다음: 리뷰 선택 <FeatherIcon icon="arrow-right" size="14"/>
        </Button>
      </div>
    </div>
  );

  renderStep2 = () => {
    const docs = this.reviewList.docs || [];
    const eligible = docs.filter(r => !this.isCopiedToAllTargets(String(r._id)));
    const allOnPageSelected = eligible.length > 0 && eligible.every(r => this.selectedReviewIds.includes(String(r._id)));
    return (
      <div className="review-copy-step">
        <div className="copy-step-titlebar">
          <h4 className="copy-step-title">STEP 2. 복사할 리뷰 선택</h4>
          <span className="copy-step-counter">{this.selectedReviewIds.length}건 선택됨</span>
        </div>

        <div className="copy-review-toolbar">
          <div className="copy-review-toolbar__search">
            <FeatherIcon icon="search" size="14" className="copy-search-icon"/>
            <FormInput placeholder="작성자/내용 검색" value={this.reviewFilters.keyword}
                       onChange={this.onChangeReviewFilter}
                       onKeyDown={e => { if (e.key === 'Enter') { this.reviewParams.page = 1; this.loadReviews(); } }}/>
          </div>
          <div className="copy-review-toolbar__filters">
            <span className="copy-rating-filter-label">별점 필터</span>
            {[5, 4, 3, 2, 1].map(r => (
              <button key={r} type="button"
                      className={`copy-rating-chip ${this.reviewFilters.ratings.includes(r) ? 'on' : ''}`}
                      onClick={() => { this.toggleRatingFilter(r); this.reviewParams.page = 1; this.loadReviews(); }}>
                {r}★
              </button>
            ))}
          </div>
        </div>

        <div className="copy-review-quickactions">
          <Button size="sm" theme={allOnPageSelected ? 'primary' : 'light'} onClick={() => this.toggleSelectAllOnPage()}>
            <FeatherIcon icon={allOnPageSelected ? 'check-square' : 'square'} size="14"/>
            <span className="ml-1">현재 페이지 전체 선택</span>
          </Button>
          <span className="copy-quickaction-divider"/>
          <Button size="sm" theme="light" onClick={() => { this.selectByRating(5); }}>5★ 추가</Button>
          <Button size="sm" theme="light" onClick={() => { this.selectByRating(4); }}>4★ 추가</Button>
          <span className="copy-quickaction-divider"/>
          <Button size="sm" theme="light" onClick={() => this.clearSelectedReviews()}>
            <FeatherIcon icon="x" size="14"/>
            <span className="ml-1">선택 해제</span>
          </Button>
        </div>

        <table className="s-table copy-review-table">
          <colgroup>
            <col style={{width: '40px'}}/>
            <col style={{width: '100px'}}/>
            <col/>
            <col style={{width: '110px'}}/>
            <col style={{width: '90px'}}/>
            <col style={{width: '110px'}}/>
          </colgroup>
          <thead>
            <tr>
              <th></th>
              <th>별점</th>
              <th>내용</th>
              <th>작성자</th>
              <th>작성일</th>
              <th>복사 상태</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted">조건에 맞는 리뷰가 없습니다.</td></tr>
            )}
            {docs.map(r => {
              const id = String(r._id);
              const fullyCopied = this.isCopiedToAllTargets(id);
              const partiallyCopied = !fullyCopied && this.isCopiedToAny(id);
              const checked = this.selectedReviewIds.includes(id);
              return (
                <tr key={id} className={fullyCopied ? 'is-disabled' : ''}>
                  <td>
                    <FormCheckbox small checked={checked} disabled={fullyCopied}
                                  onChange={() => this.toggleSelectReview(id)}/>
                  </td>
                  <td>{this.renderRatingStars(r.goodsPt)}</td>
                  <td>
                    <div className="copy-review-content-cell">
                      {(() => {
                        const imgs = this.extractReviewImages(r);
                        if (!imgs.length) return null;
                        const visible = imgs.slice(0, 3);
                        const moreCount = imgs.length - visible.length;
                        return (
                          <div className="copy-review-thumbs">
                            {visible.map((u, i) => (
                              <img key={i} src={u} alt="" className="copy-review-thumb"
                                   onError={e => { e.target.style.visibility = 'hidden'; }}/>
                            ))}
                            {moreCount > 0 && <span className="copy-review-thumb copy-review-thumb--more">+{moreCount}</span>}
                          </div>
                        );
                      })()}
                      <span className="copy-review-contents" title={r.contents || ''}>{(r.contents || '').replace(/<br\s*\/?>/gi, ' ')}</span>
                    </div>
                  </td>
                  <td>{r.writerName}</td>
                  <td>{r.writerAt ? moment(r.writerAt).format('YY-MM-DD') : '-'}</td>
                  <td>
                    {fullyCopied && <span className="copy-status-badge copy-status-badge--copied">전체 복사됨</span>}
                    {partiallyCopied && <span className="copy-status-badge copy-status-badge--partial">일부 복사됨</span>}
                    {!fullyCopied && !partiallyCopied && <span className="copy-status-badge copy-status-badge--new">미복사</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="copy-review-paging">
          <Button size="sm" theme="light" disabled={this.reviewList.page <= 1} onClick={() => this.goReviewPage(this.reviewList.page - 1)}>이전</Button>
          <span className="mx-2">{this.reviewList.page} / {this.reviewList.totalPages}</span>
          <Button size="sm" theme="light" disabled={this.reviewList.page >= this.reviewList.totalPages} onClick={() => this.goReviewPage(this.reviewList.page + 1)}>다음</Button>
        </div>

        <div className="copy-step-actions">
          <Button theme="light" onClick={() => this.goStep(1)}>
            <FeatherIcon icon="arrow-left" size="14"/> 이전
          </Button>
          <Button className="ml-2" onClick={() => this.goStep(3)}>
            다음: 옵션/실행 <FeatherIcon icon="arrow-right" size="14"/>
          </Button>
        </div>
      </div>
    );
  };

  renderStep3 = () => (
    <div className="review-copy-step">
      <h4 className="copy-step-title">STEP 3. 복사 옵션 및 실행</h4>

      <Card className="copy-options-card">
        <CardBody>
          <div className="copy-summary">
            <div><strong>원본:</strong> {this.sourceProduct && this.sourceProduct.productName} ({this.sourceProduct && this.sourceProduct.platformProductId})</div>
            <div><strong>대상:</strong> {this.targetProducts.map(t => t.productName).join(', ')} ({this.targetProducts.length}개)</div>
            <div><strong>선택 리뷰:</strong> {this.selectedReviewIds.length}건</div>
            <div><strong>총 복사:</strong> {this.totalToCopy}건 ({this.effectiveChunkSize}건씩 {Math.ceil(this.selectedReviewIds.length / this.effectiveChunkSize)}청크 처리)</div>
          </div>

          <div className="copy-options-grid">
            <div className="copy-option-row">
              <FormCheckbox toggle small checked={this.options.keepWriter}
                            onChange={() => this.options.keepWriter = !this.options.keepWriter}>
                작성자 이름 원본 유지
              </FormCheckbox>
              <p className="copy-option-help">
                {this.options.keepWriter ? '원본 리뷰의 작성자 이름을 그대로 사용합니다.' : `현재 실행자(${this.teamUser.name || this.teamUser.userName || '관리자'})를 작성자로 기록합니다.`}
              </p>
            </div>
            <div className="copy-option-row">
              <FormCheckbox toggle small checked={this.options.keepWriterAt}
                            onChange={() => this.options.keepWriterAt = !this.options.keepWriterAt}>
                작성일 원본 유지
              </FormCheckbox>
              <p className="copy-option-help">
                {this.options.keepWriterAt ? '원본 작성일을 카페24 created_date 로 사용합니다.' : '복사 실행 시각을 작성일로 기록합니다.'}
              </p>
            </div>
            <div className="copy-option-row">
              <FormCheckbox toggle small checked={this.options.includeImages}
                            onChange={() => this.options.includeImages = !this.options.includeImages}>
                리뷰 이미지 포함
              </FormCheckbox>
              <p className="copy-option-help">
                {this.options.includeImages ? '원본 이미지 URL을 그대로 카페24 attach_file_urls 로 첨부합니다.' : '이미지 없이 텍스트/별점만 복사합니다.'}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="copy-warning-notice">
        <FeatherIcon icon="alert-triangle" size="16"/>
        <span>복사된 리뷰는 카페24 게시판에 실제로 작성됩니다. 실행 후 되돌리려면 "리뷰 복사 이력" 페이지에서 일괄 삭제할 수 있습니다.</span>
      </div>

      {!this.warningAgreed && (
        <div className="copy-agree-hint">
          <FeatherIcon icon="alert-circle" size="12" aria-hidden="true"/>
          유의사항에 동의해야 복사를 실행할 수 있습니다
        </div>
      )}

      <div className="copy-step-actions">
        <Button theme="light" onClick={() => this.goStep(2)}>
          <FeatherIcon icon="arrow-left" size="14"/> 이전
        </Button>
        <Button className="ml-2" theme="primary"
                disabled={!this.warningAgreed}
                title={!this.warningAgreed ? '유의사항에 먼저 동의해 주세요' : ''}
                onClick={() => this.openProgressAndRun()}>
          <FeatherIcon icon="copy" size="14"/> {this.totalToCopy}건 복사 실행
        </Button>
      </div>
    </div>
  );

  render() {
    if (!this.isLogin) {
      return (
        <div className="main-content">
          <PageTitle/>
          <div className="widget-box"><p className="text-center p-4">로그인이 필요합니다.</p></div>
        </div>
      );
    }

    return (
      <div className="main-content">
        <PageTitle/>
        <div className="widget-box review-copy-wizard">

          {this.showWarning && (
            <div className="rw-alert-banner" role="alert">
              <div className="rw-alert-icon" aria-hidden="true">
                <FeatherIcon icon="alert-triangle" size="18"/>
              </div>
              <div className="rw-alert-body">
                <div className="rw-alert-text">
                  <strong>유의사항</strong>
                  <p>해당 리뷰 복사에 대한 책임은 작성자 또는 작성자가 속한 회사에 있습니다. 복사된 리뷰로 인해 발생하는 모든 법적 분쟁의 책임은 작성자에게 귀속됩니다.</p>
                </div>
                <div className="rw-alert-actions">
                  <label className="rw-alert-agree" htmlFor="rc-warning-agree">
                    <input
                      id="rc-warning-agree"
                      type="checkbox"
                      checked={this.warningAgreed}
                      onChange={() => this.toggleWarningAgree()}
                    />
                    <span className="rw-alert-agree-box" aria-hidden="true">
                      {this.warningAgreed && <FeatherIcon icon="check" size="11"/>}
                    </span>
                    <span className="rw-alert-agree-label">위 유의사항에 동의합니다</span>
                  </label>
                  <button type="button"
                          className="rw-alert-confirm-btn"
                          disabled={!this.warningAgreed}
                          onClick={() => this.dismissWarning()}
                          aria-label={this.warningAgreed ? '동의하고 닫기' : '동의 후 닫기 가능'}>
                    <FeatherIcon icon="check" size="13" aria-hidden="true"/>
                    동의하고 닫기
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="widget-page-tabs">
            <div className="page-tab-header copy-page-header">
              <div className="copy-page-header__text">
                <p>A 상품의 리뷰를 선택하여 B 상품(들)으로 복제합니다. 카페24에 실제 리뷰가 작성됩니다.</p>
              </div>
              <Link to="/reviewCopyHistory" className="copy-history-btn">
                <Button size="sm" theme="light">
                  <FeatherIcon icon="clock" size="14"/>
                  <span style={{marginLeft: 4}}>작업 이력 보기</span>
                </Button>
              </Link>
            </div>
            <div className="copy-step-indicator">
              {[
                {n: 1, label: '상품 선택'},
                {n: 2, label: '리뷰 선택'},
                {n: 3, label: '옵션 / 실행'}
              ].map(s => (
                <div key={s.n} className={`copy-step-pill ${this.activeStep === s.n ? 'active' : ''} ${this.activeStep > s.n ? 'done' : ''}`}>
                  <span className="copy-step-pill__num">{s.n}</span>
                  <span className="copy-step-pill__label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {this.activeStep === 1 && this.renderStep1()}
          {this.activeStep === 2 && this.renderStep2()}
          {this.activeStep === 3 && this.renderStep3()}

        </div>

        {/* 상품 검색 모달 */}
        <Modal size="lg" open={this.productSearch.isOpen} toggle={() => {}} centered
               className="copy-product-search-modal">
          <ModalHeader>{this.productSearch.side === 'source' ? '원본 상품 선택' : '대상 상품 선택 (다중 가능)'}</ModalHeader>
          <ModalBody className="copy-product-search-modal-body">
            <Row>
              <Col md="10">
                <FormInput placeholder="상품명 / 상품번호 검색"
                           value={this.productSearch.keyword}
                           onChange={this.onChangeProductKeyword}
                           onKeyDown={e => { if (e.key === 'Enter') { this.productSearch.page = 1; this.searchProducts(); } }}/>
              </Col>
              <Col md="2">
                <Button block onClick={() => { this.productSearch.page = 1; this.searchProducts(); }}>검색</Button>
              </Col>
            </Row>
            <table className="s-table copy-product-search-table">
              <colgroup>
                <col style={{width: '46px'}}/>
                <col style={{width: '70px'}}/>
                <col style={{width: '100px'}}/>
                <col/>
                <col style={{width: '90px'}}/>
              </colgroup>
              <thead>
                <tr><th></th><th>이미지</th><th>상품번호</th><th>상품명</th><th></th></tr>
              </thead>
              <tbody>
                {this.productSearch.isSearching && <tr><td colSpan={5} className="text-center">검색 중…</td></tr>}
                {!this.productSearch.isSearching && this.productSearch.results.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted">결과 없음</td></tr>
                )}
                {this.productSearch.results.map(p => {
                  const isSrc = this.sourceProduct && this.sourceProduct.platformProductId === String(p.platformProductId);
                  const isTgt = this.isTargetPicked(String(p.platformProductId));
                  const isPicked = this.productSearch.side === 'source' ? isSrc : isTgt;
                  const thumbUrl = p.image || (p.images && (p.images.small || p.images.thumbnail || p.images.tiny || p.images.detail)) || '';
                  return (
                    <tr key={p.platformProductId} className={isPicked ? 'is-picked' : ''}>
                      <td className="text-center">
                        {this.productSearch.side === 'target'
                          ? <FormCheckbox small checked={isTgt} onChange={() => this.pickProduct(p)} className="m-0"/>
                          : <span className={`copy-product-search-radio ${isSrc ? 'on' : ''}`}
                                  onClick={() => this.pickProduct(p)}/>}
                      </td>
                      <td className="text-center">{this.renderThumb(thumbUrl)}</td>
                      <td>{p.platformProductId}</td>
                      <td title={p.productName}>{p.productName}</td>
                      <td>
                        <Button size="sm" theme={isPicked ? 'primary' : 'light'}
                                onClick={() => this.pickProduct(p)}>
                          {isPicked ? '선택됨' : '선택'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="copy-product-search-paging">
              <Button size="sm" theme="light" disabled={this.productSearch.page <= 1} onClick={() => this.goProductPage(this.productSearch.page - 1)}>이전</Button>
              <span className="mx-2">{this.productSearch.page} / {this.productSearch.totalPages}</span>
              <Button size="sm" theme="light" disabled={this.productSearch.page >= this.productSearch.totalPages} onClick={() => this.goProductPage(this.productSearch.page + 1)}>다음</Button>
            </div>
            <div className="text-center mt-3">
              <Button theme="primary" onClick={this.closeProductSearch}>완료</Button>
            </div>
          </ModalBody>
        </Modal>

        {/* 진행률 모달 */}
        <Modal size="md" open={this.progress.isOpen} toggle={() => {}} centered>
          <ModalHeader>리뷰 복사 진행</ModalHeader>
          <ModalBody>
            <div className="copy-progress-modal">
              <div className="copy-progress-stats">
                <div><span>총</span><strong>{this.progress.totalCount}</strong></div>
                <div><span>성공</span><strong className="text-success">{this.progress.successCount}</strong></div>
                <div><span>실패</span><strong className="text-danger">{this.progress.failCount}</strong></div>
                <div><span>스킵</span><strong className="text-muted">{this.progress.skipCount}</strong></div>
              </div>
              <div className="copy-progress-bar">
                <div className="copy-progress-bar__fill"
                     style={{width: `${this.progress.chunkTotal > 0 ? (this.progress.chunkIndex / this.progress.chunkTotal) * 100 : 0}%`}}/>
              </div>
              <div className="copy-progress-text">
                청크 {this.progress.chunkIndex} / {this.progress.chunkTotal}
                {this.progress.isRunning ? ' — 처리 중…' : ' — 종료'}
              </div>
              <div className="copy-progress-log">
                {this.progress.log.slice(-12).map((l, i) => (
                  <div key={i}><span className="copy-progress-log__time">[{l.time}]</span> {l.msg}</div>
                ))}
              </div>
              <div className="text-center mt-3">
                {this.progress.isRunning && (
                  <Button theme="warning" onClick={this.requestCancel} disabled={this.progress.isCancelRequested}>
                    {this.progress.isCancelRequested ? '중단 대기 중…' : '중단'}
                  </Button>
                )}
                {!this.progress.isRunning && (
                  <>
                    <Link to={this.progress.jobId ? `/reviewCopyHistory?jobId=${this.progress.jobId}` : '/reviewCopyHistory'}>
                      <Button theme="light">작업 이력 보기</Button>
                    </Link>
                    <Button className="ml-2" theme="primary" onClick={this.closeProgress}>닫기</Button>
                  </>
                )}
              </div>
            </div>
          </ModalBody>
        </Modal>

        <ModalLoading isLoading={this.isLoading}/>
      </div>
    );
  }
}

export default ReviewCopy;
